// src/api/generalAgentApi.ts

import { ChatStreamCallbacks, ChatResponseChunk } from "../interfaces/chatTypes";
import { ChatApiError } from "./chatApi";
import { handleApiErrorResponse } from "./utils/errorHandler";
import { createLogger } from "../utils/logger";
import { constructRequest } from "./utils/apiHandler";
import { normalizeAccessToken } from "../utils/typeHelpers";
import { createClientErrorInfo } from "./utils/clientErrorHandler";
import { routeChunkToCallback, ChunkRouterState } from "./utils/chunkRouter";
import { streamErrorToPostHog, ApiNames } from "./utils/captureError";
const log = createLogger("GeneralAgentApi");

// Base URL for the API
const API_BASE_URL = process.env.API_BASE_URL;

/**
 * General Agent API Request Interface
 *
 * This interface matches the general agent API specification
 * with support for multiple additional files
 */
interface GeneralAgentRequest {
  message: string;
  full_text_file: File;
  additional_context?: File[]; // Multiple context files
  selected_text?: File;
  id?: string;
  bypass_cache?: boolean;
  details?: any;
  chat_history?: any[];
  access_token?: string; // User access token
}

/**
 * General Agent API Client
 *
 * Handles communication with the general agent endpoint
 * for the CCM Space agent
 */
class GeneralAgentApi {
  private abortController: AbortController | null = null;
  private routerState: ChunkRouterState = {};

  /**
   * Send a streaming chat request to the general agent API
   *
   * @param request The general agent request with document and context files
   * @param callbacks Callbacks for handling streaming response
   */
  async sendStreamingChatRequest(
    request: GeneralAgentRequest,
    callbacks: ChatStreamCallbacks
  ): Promise<void> {
    // Log the request details to debug web search
    log.info("🔍 GENERAL AGENT API REQUEST:", {
      hasDetails: !!request.details,
      webSearchFlag: request.details?.web_search,
      details: request.details,
    });

    log.info("Sending general agent chat request", {
      hasMessage: !!request.message,
      hasFullTextFile: !!request.full_text_file,
      additionalContextCount: request.additional_context?.length || 0,
      hasSelectedText: !!request.selected_text,
      sessionId: request.id,
    });
    // Create abort controller for cancellation support
    this.abortController = new AbortController();
    // Reset router state for new streaming session
    this.routerState = {};

    // Prepare form data
    const formData = new FormData();
    formData.append("message", request.message);
    formData.append("full_text_file", request.full_text_file);

    // Add additional context files if provided
    if (request.additional_context && request.additional_context.length > 0) {
      request.additional_context.forEach((file) => {
        formData.append(`additional_context`, file);
      });
    }

    // Add selected text if provided
    if (request.selected_text) {
      formData.append("selected_text", request.selected_text);
    }

    // Add optional parameters
    if (request.id) {
      formData.append("id", request.id);
    }

    if (request.bypass_cache !== undefined) {
      formData.append("bypass_cache", request.bypass_cache.toString());
    }

    if (request.details) {
      // Log the details being sent for debugging the new features
      log.debug("Sending enhanced details configuration", {
        web_search: request.details.web_search,
        thinking: request.details.thinking,
        benchmark_mode: request.details.benchmark_mode,
      });

      formData.append("details", JSON.stringify(request.details));
    }

    if (request.chat_history && request.chat_history.length > 0) {
      formData.append("chat_history", JSON.stringify(request.chat_history));
    }

    const url = `${API_BASE_URL}/chat/general_agent/stream/`;

    try {
      log.info("Making request to general agent API", { url });

      callbacks.onStart();

      const api_request = constructRequest(
        url,
        "POST",
        normalizeAccessToken(request.access_token),
        formData,
        this.abortController.signal
      );
      const response = await fetch(api_request);

      log.info("Received response from general agent API", {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok,
      });

      if (!response.ok) {
        log.error("General agent API returned error response", {
          status: response.status,
          statusText: response.statusText,
        });

        // Use shared utility to parse and handle error response
        const apiError = await handleApiErrorResponse(response);
        streamErrorToPostHog(apiError, ApiNames.GENERAL_AGENT_API);
        callbacks.onError(apiError);
        return;
      }

      // Handle streaming response
      const reader = response.body?.getReader();
      if (!reader) {
        const apiError = new ChatApiError("No response body", "NO_RESPONSE_BODY", 500);
        streamErrorToPostHog(apiError, ApiNames.GENERAL_AGENT_API);
        callbacks.onError(apiError);
        return;
      }

      const decoder = new TextDecoder();
      let buffer = "";

      callbacks.onMessageStart?.();

      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          log.info("General agent streaming completed");
          break;
        }

        // Decode chunk and add to buffer
        buffer += decoder.decode(value, { stream: true });

        // Process complete lines
        const lines = buffer.split("\n");
        buffer = lines.pop() || ""; // Keep incomplete line in buffer

        for (const line of lines) {
          if (line.trim() === "") continue;

          try {
            // Parse SSE format
            if (line.startsWith("data: ")) {
              const jsonData = line.slice(6); // Remove "data: " prefix
              if (jsonData === "[DONE]") {
                log.debug("Received [DONE] marker");
                continue;
              }

              const chunk: ChatResponseChunk = JSON.parse(jsonData);
              this.handleChunk(chunk, callbacks);
            }
          } catch (parseError) {
            log.warn("Failed to parse chunk", {
              line,
              error: parseError instanceof Error ? parseError.message : String(parseError),
            });
          }
        }
      }

      callbacks.onComplete();
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") {
        log.info("General agent request was cancelled");
        return;
      }

      log.error("General agent API request failed", {
        error: error instanceof Error ? error.message : String(error),
        errorName: error instanceof Error ? error.name : "Unknown",
        url: url,
        stack: error instanceof Error ? error.stack : undefined,
      });

      if (error instanceof ChatApiError) {
        streamErrorToPostHog(error, ApiNames.GENERAL_AGENT_API, undefined, {
          chat_id: request.id,
        });
        callbacks.onError(error);
      } else {
        // Handle client-side errors with helpful, contextual messages
        const clientError = createClientErrorInfo(error, "streaming");
        const chatApiError = new ChatApiError(clientError.message, clientError.errorCode, 0);
        // Also capture the ORIGINAL error: the wrapper only carries the
        // user-facing message and the CONNECTION_FAILED code, so
        // without this we never see the underlying message (e.g. "Failed to
        // fetch"). chat_id joins this to the BE "Stream outcome" log.
        streamErrorToPostHog(chatApiError, ApiNames.GENERAL_AGENT_API, undefined, {
          chat_id: request.id,
          original_error_name: error instanceof Error ? error.name : undefined,
          original_error_message: error instanceof Error ? error.message : String(error),
        });
        callbacks.onError(chatApiError);
      }
    } finally {
      this.abortController = null;
    }
  }

  /**
   * Handle individual streaming chunks
   */
  private handleChunk(chunk: ChatResponseChunk, callbacks: ChatStreamCallbacks): void {
    // Use shared chunk router with standard configuration and persistent state
    routeChunkToCallback(chunk, callbacks, log, {
      verboseAutoRedlineLogging: true,
      verboseWebSearchLogging: true,
      enhancedErrorHandling: true,
      enableFallbackToText: true,
      state: this.routerState,
    });
  }

  /**
   * Cancel any ongoing streaming request
   */
  cancelRequest(): void {
    if (this.abortController) {
      log.info("Cancelling general agent request");
      this.abortController.abort();
      this.abortController = null;
    }
  }
}

// Export singleton instance
export const generalAgentApi = new GeneralAgentApi();
